import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useLandingPagesQuery,
  useCreateLandingPageMutation,
  useSaveLandingPageMutation,
  useDeleteLandingPageMutation
} from "@queries/landing_page";
import ImageUploader from "@components/upload/ImageUploader";
import config from "@config/config";
import "@styles/opencart-admin.css";

const defaultForm = {
  name: "",
  name_ar: "",
  description: "",
  description_ar: "",
  icon: "",
  icon_ar: "",
  deeplink: "",
  is_banner: false,
  is_enabled: true,
  sort_order: 1
};

const imageBaseUrl = (config.imageBaseUrl || "").replace(/\/+$/, "") + "/";

export default function LandingPagePage() {
  const [filters, setFilters] = React.useState({
    name: "",
    name_ar: "",
    is_enabled: "",
    is_banner: ""
  });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);

  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error
  } = useLandingPagesQuery(
    {
      name: filters.name || undefined,
      name_ar: filters.name_ar || undefined,
      is_enabled: filters.is_enabled || undefined,
      is_banner: filters.is_banner || undefined
    },
    page,
    pageSize
  );

  const createMutation = useCreateLandingPageMutation();
  const saveMutation = useSaveLandingPageMutation();
  const deleteMutation = useDeleteLandingPageMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() {
    setEditingId(null);
    setForm(defaultForm);
    setShowForm(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      name_ar: row.name_ar || "",
      description: row.description || "",
      description_ar: row.description_ar || "",
      icon: row.icon || "",
      icon_ar: row.icon_ar || "",
      deeplink: row.deeplink || "",
      is_banner: !!row.is_banner,
      is_enabled: !!row.is_enabled,
      sort_order: row.sort_order || 1
    });
    setShowForm(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    const payload = {
      ...form,
      is_banner: form.is_banner ? 1 : 0,
      is_enabled: form.is_enabled ? 1 : 0,
      sort_order: Number(form.sort_order) || 0
    };

    if (editingId) {
      await saveMutation.mutateAsync({ id: editingId, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    setShowForm(false);
    setEditingId(null);
    queryClient.invalidateQueries({ queryKey: ["catalog", "landing-page"] });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this landing page item?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({ queryKey: ["catalog", "landing-page"] });
  }

  function handleFilterChange(field, value) {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Landing Page</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Landing Page</span>
        </nav>
      </div>

      <div className="oc-card">
        <div>
          <div className="oc-toolbar" style={{ marginBottom: 12 }}>
            <div>
              <strong>Landing Page</strong>
            </div>
            <div>
              <button className="oc-btn primary" onClick={openCreate}>
                + Add
              </button>
            </div>
          </div>

          {isError && (
            <div style={{ color: "#c00", marginBottom: 8 }}>
              Error: {error?.message || "Unknown error"}
            </div>
          )}

          <table className="oc-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>ID</th>
                <th>Name</th>
                <th>Arabic</th>
                <th>Deeplink</th>
                <th>Icon</th>
                <th>Arabic icon</th>
                <th style={{ width: 90 }}>Sort</th>
                <th style={{ width: 90 }}>Banner</th>
                <th style={{ width: 90 }}>Enabled</th>
                <th style={{ width: 140 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: 24 }}>
                    No items
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td dir="rtl">{row.name_ar}</td>
                  <td>{row.deeplink}</td>
                  <td>
                    {row.icon && (
                      <img
                        src={imageBaseUrl + row.icon}
                        alt=""
                        className="oc-thumb"
                      />
                    )}
                  </td>
                  <td>
                    {row.icon_ar && (
                      <img
                        src={imageBaseUrl + row.icon_ar}
                        alt=""
                        className="oc-thumb"
                      />
                    )}
                  </td>
                  <td>{row.sort_order}</td>
                  <td>
                    <span
                      className={
                        row.is_banner ? "oc-badge yes" : "oc-badge no"
                      }
                    >
                      {row.is_banner ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        row.is_enabled ? "oc-badge yes" : "oc-badge no"
                      }
                    >
                      {row.is_enabled ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="oc-btn blue"
                        onClick={() => openEdit(row)}
                      >
                        edit
                      </button>
                      <button
                        className="oc-btn danger"
                        onClick={() => handleDelete(row.id)}
                      >
                        delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="oc-card" style={{ display: "None" }}>
          <div className="title">Filter</div>
          <div className="form-group">
            <label>Name</label>
            <input
              className="oc-input"
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Arabic name</label>
            <input
              className="oc-input"
              value={filters.name_ar}
              onChange={(e) => handleFilterChange("name_ar", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Enabled</label>
            <select
              className="oc-input"
              value={filters.is_enabled}
              onChange={(e) => handleFilterChange("is_enabled", e.target.value)}
            >
              <option value="">All</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
          <div className="form-group">
            <label>Banner</label>
            <select
              className="oc-input"
              value={filters.is_banner}
              onChange={(e) => handleFilterChange("is_banner", e.target.value)}
            >
              <option value="">All</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>

      {showForm && (
        <div className="oc-modal-backdrop">
          <div className="oc-modal">
            <div className="oc-modal-header">
              <h2>{editingId ? "Edit landing item" : "Add landing item"}</h2>
            </div>
            <form onSubmit={handleSave}>
              <div className="oc-modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      className="oc-input"
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Arabic name</label>
                    <input
                      className="oc-input"
                      dir="rtl"
                      value={form.name_ar}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name_ar: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      className="oc-input"
                      value={form.description}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          description: e.target.value
                        }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Arabic description</label>
                    <input
                      className="oc-input"
                      dir="rtl"
                      value={form.description_ar}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          description_ar: e.target.value
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Icon</label>
                    <div className="oc-image-input-row">
                      <input
                        className="oc-input"
                        value={form.icon}
                        placeholder="image path"
                        onChange={(e) =>
                          setForm((f) => ({ ...f, icon: e.target.value }))
                        }
                      />
                      <ImageUploader
                        value={form.icon}
                        onChange={(newPath) => {
                          if (newPath) {
                            setForm((f) => ({ ...f, icon: newPath }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Arabic icon</label>
                    <div className="oc-image-input-row">
                      <input
                        className="oc-input"
                        value={form.icon_ar}
                        placeholder="image path"
                        onChange={(e) =>
                          setForm((f) => ({ ...f, icon_ar: e.target.value }))
                        }
                      />
                      <ImageUploader
                        value={form.icon_ar}
                        onChange={(newPath) => {
                          if (newPath) {
                            setForm((f) => ({ ...f, icon_ar: newPath }));
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Deeplink</label>
                    <input
                      className="oc-input"
                      value={form.deeplink}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, deeplink: e.target.value }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Sort order</label>
                    <input
                      type="number"
                      className="oc-input"
                      value={form.sort_order}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          sort_order: e.target.value
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.is_banner}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            is_banner: e.target.checked
                          }))
                        }
                      />
                      Banner
                    </label>
                  </div>
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.is_enabled}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            is_enabled: e.target.checked
                          }))
                        }
                      />
                      Enabled
                    </label>
                  </div>
                </div>
              </div>
              <div className="oc-modal-footer">
                <button
                  type="button"
                  className="oc-btn ghost"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="oc-btn primary"
                  disabled={
                    createMutation.isLoading || saveMutation.isLoading
                  }
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

